import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { AccountEntitlementsApiService } from './account-entitlements-api.service';
import { PlanUsageApiService } from './plan-usage-api.service';
import { FrontendLoggerService } from '../../../services/frontend-logger.service';

/**
 * Resuelve si el plan efectivo de la cuenta actual tiene una feature habilitada.
 * Reusa los endpoints existentes del middleware: `dashboard/account-usage` (para
 * obtener el accountId actual) y `accounts/{id}/entitlements/evaluate` (dry-run).
 * Degrada a "sin feature" (false) ante cualquier error de red/backend; los errores
 * no se cachean para que un reintento posterior pueda recuperarse.
 */
@Injectable({ providedIn: 'root' })
export class PlanFeatureService {
  private readonly cache = new Map<string, Observable<boolean>>();

  constructor(
    private readonly usageApi: PlanUsageApiService,
    private readonly entitlementsApi: AccountEntitlementsApiService,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  hasFeature(featureCode: string): Observable<boolean> {
    let cached = this.cache.get(featureCode);
    if (!cached) {
      cached = this.usageApi.getAccountUsage().pipe(
        switchMap((usage) => this.entitlementsApi.evaluateFeature(usage.accountId, featureCode)),
        map((evaluation) => evaluation.allowed === true),
        catchError((err) => {
          this.cache.delete(featureCode);
          this.frontendLogger.warn(
            `[PlanFeatureService] Could not evaluate feature "${featureCode}"; degrading to disabled. ` +
            ((err as { message?: string })?.message || '')
          );
          return of(false);
        }),
        shareReplay(1)
      );
      this.cache.set(featureCode, cached);
    }
    return cached;
  }
}
